import styled, { keyframes } from "styled-components";
import { glassDeep, glassHover } from "../common/glassmorphism";
import { Card, CardWithHeader } from "../common/cards";
import { Button as BaseButton } from "../common/buttons";

// Space-themed animations
const titleGlow = keyframes`
  0% { text-shadow: 0 0 10px rgba(99, 102, 241, 0.5), 0 0 20px rgba(99, 102, 241, 0.3); }
  50% { text-shadow: 0 0 20px rgba(99, 102, 241, 0.8), 0 0 30px rgba(99, 102, 241, 0.5); }
  100% { text-shadow: 0 0 10px rgba(99, 102, 241, 0.5), 0 0 20px rgba(99, 102, 241, 0.3); }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// Dashboard Container and Layout Components
export const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: "Segoe UI", "Roboto", "Helvetica Neue", sans-serif;
`;

export const SpaceBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  background-image: radial-gradient(
      1px 1px at 5% 5%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 25% 20%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 50% 5%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 75% 25%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 95% 15%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 10% 40%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 30% 65%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 55% 45%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 80% 70%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 90% 55%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 15% 80%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 35% 95%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 60% 85%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 85% 90%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    ),
    radial-gradient(
      1px 1px at 95% 80%,
      rgba(255, 255, 255, 0.6) 1px,
      transparent 0
    );
  opacity: 0.3;
`;

export const WelcomeSection = styled.div`
  margin-bottom: 2rem;
  padding: 2rem;
  background: linear-gradient(
    135deg,
    rgba(41, 121, 255, 0.2) 0%,
    rgba(77, 171, 247, 0.3) 100%
  );
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 16px;
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.18);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 10% 20%,
        rgba(28, 175, 252, 0.1) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 30%,
        rgba(117, 91, 255, 0.15) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 30% 70%,
        rgba(252, 99, 251, 0.1) 0%,
        transparent 40%
      );
    z-index: -1;
  }

  &::after {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 65%,
      rgba(255, 255, 255, 0.08) 75%,
      transparent 85%
    );
    animation: lightSweep 8s infinite linear;
    z-index: 1;
    pointer-events: none;
  }

  @keyframes lightSweep {
    0% {
      transform: rotate(0deg) translateX(0%);
    }
    100% {
      transform: rotate(360deg) translateX(10%);
    }
  }
`;

export const WelcomeTitle = styled.h2`
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 0.8rem 0;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-shadow: 0 0 15px rgba(114, 137, 218, 0.5);
  position: relative;
  z-index: 2;
`;

export const WelcomeText = styled.p`
  font-size: 1.1rem;
  margin: 0;
  max-width: 600px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  position: relative;
  z-index: 2;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.8), transparent);
    border-radius: 1px;
  }
`;

export const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

export const GridColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

export const DashboardCard = styled(Card)`
  ${glassDeep}
  ${glassHover}
  padding: 1.5rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 32px rgba(31, 38, 135, 0.2);
  }

  h3 {
    color: rgba(255, 255, 255, 0.95);
    margin-bottom: 1rem;
    font-size: 1.25rem;
    font-weight: 500;
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 1rem;
  }
`;

export const NotificationBar = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 2rem;
  border-radius: 8px;
  background: ${(props) =>
    props.type === "error"
      ? "rgba(239, 68, 68, 0.9)"
      : "rgba(34, 197, 94, 0.9)"};
  color: white;
  font-weight: 500;
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

export const WorkshopsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const WorkshopItem = styled.div`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(5px);
  }
`;

export const WorkshopDate = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
`;

export const WorkshopTitle = styled.h4`
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
`;

export const WorkshopLocation = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
`;

export const RegisteredBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
  border-radius: 4px;
  font-size: 0.8rem;
  margin-top: 0.5rem;
`;

export const ViewAllButton = styled(BaseButton)`
  margin-top: 1rem;
  width: 100%;
  background: rgba(99, 102, 241, 0.2);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.3);

  &:hover {
    background: rgba(99, 102, 241, 0.3);
    transform: translateY(-2px);
  }
`;

export const CounselorsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const CounselorItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(5px);
  }
`;

export const CounselorImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
`;

export const CounselorInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const CounselorName = styled.h4`
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 0.25rem 0;
  font-size: 1.1rem;
`;

export const CounselorSpecialty = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.25rem;
`;

export const CounselorAvailability = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
`;

export const ScheduleButton = styled(BaseButton)`
  background: rgba(99, 102, 241, 0.2);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.3);
  padding: 0.5rem 1rem;
  font-size: 0.9rem;

  &:hover {
    background: rgba(99, 102, 241, 0.3);
    transform: translateY(-2px);
  }
`;

export const AppointmentRequestForm = styled.div`
  display: ${(props) => (props.isVisible ? "block" : "none")};
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  h4 {
    color: rgba(255, 255, 255, 0.9);
    margin: 0 0 1rem 0;
  }
`;

export const FormInputGroup = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 0.5rem;
  }

  input {
    width: 100%;
    padding: 0.75rem;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    color: white;

    &:focus {
      outline: none;
      border-color: rgba(99, 102, 241, 0.5);
      box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
    }
  }
`;

export const FormButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

export const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 1.5rem 0;
`;

export const AppointmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const AppointmentItem = styled.div`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(5px);
  }
`;

export const AppointmentDate = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
`;

export const AppointmentReason = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.5rem;
`;

export const AppointmentStatus = styled.div`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  background: ${(props) =>
    props.status === "accepted"
      ? "rgba(34, 197, 94, 0.2)"
      : props.status === "rejected"
      ? "rgba(239, 68, 68, 0.2)"
      : "rgba(234, 179, 8, 0.2)"};
  color: ${(props) =>
    props.status === "accepted"
      ? "#4ade80"
      : props.status === "rejected"
      ? "#f87171"
      : "#eab308"};
`;

// Workshop Components
export const SuggestedCompaniesSection = styled.div`
  width: 100%;
  overflow: hidden;
`;

// Notification Components
export const Notification = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #4caf50;
  color: white;
  padding: 15px 25px;
  border-radius: 5px;
  z-index: 1000;
  animation: fadeIn 0.3s ease-in-out;
`;

// Call Components
export const CallContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const CallVideoArea = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const MainVideoFeed = styled.div`
  width: 80%;
  height: 70%;
  background-color: #1a1a1a;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background-color: #1a1a1a;
  }
`;

export const VideoOffMessage = styled.div`
  color: white;
  font-size: 1.2rem;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.5);
  padding: 1rem 2rem;
  border-radius: 8px;
  backdrop-filter: blur(4px);
`;

export const CallControls = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 15px;
`;

export const ControlButton = styled(BaseButton)`
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #333;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #444;
  }
`;

export const EndCallButton = styled(ControlButton)`
  background-color: #dc3545;

  &:hover {
    background-color: #c82333;
  }
`;

export const CallButton = styled(BaseButton)`
  padding: 15px 30px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1.1rem;
  transition: background-color 0.3s;

  &:hover {
    background-color: #0056b3;
  }
`;

export const CallStatus = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 20px 40px;
  border-radius: 10px;
  font-size: 1.2rem;
`;

export const DashboardContent = styled.div`
  margin-top: 20px;
`;

// Company views components
export const CompanyViewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 15px;
`;

export const CompanyViewItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(99, 102, 241, 0.4);
    background-color: rgba(99, 102, 241, 0.08);
  }
`;

export const CompanyViewIcon = styled.div`
  font-size: 24px;
  margin-right: 15px;
  color: #666;
`;

export const CompanyViewDetails = styled.div`
  flex: 1;
`;

export const CompanyName = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

export const ViewDate = styled.div`
  font-size: 14px;
  color: #888;
`;

// Empty state component
export const EmptyState = styled.div`
  text-align: center;
  padding: 30px 20px;
  color: #666;

  p {
    margin-bottom: 15px;
  }
`;

// Modal components
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

export const ModalContent = styled.div`
  background-color: #fff;
  border-radius: 10px;
  padding: 0;
  width: 90%;
  max-width: ${(props) => (props.small ? "400px" : "600px")};
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
`;

export const ModalHeader = styled.div`
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  position: relative;

  h3 {
    margin: 0;
    font-size: 18px;
  }
`;

export const ModalBody = styled.div`
  padding: 20px;

  p {
    margin: 10px 0;
  }
`;

export const ModalFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

export const CloseButton = styled.button`
  position: absolute;
  right: 15px;
  top: 15px;
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

// Video call components
export const CallModalContent = styled.div`
  background-color: #222;
  border-radius: 10px;
  width: 90%;
  max-width: 1000px;
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
`;

export const SmallVideoFeed = styled.div`
  position: absolute;
  width: 150px;
  height: 100px;
  bottom: 20px;
  right: 20px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.2);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const VideoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #333;
  color: white;
  font-size: 24px;
`;

export const CallInfo = styled.div`
  padding: 10px 20px;
  color: #aaa;
  text-align: center;
  font-size: 14px;
`;

export const CallWarning = styled.div`
  background-color: rgba(255, 152, 0, 0.2);
  color: #ff9800;
  padding: 8px 16px;
  border-radius: 6px;
  margin: 10px 0;
  text-align: center;
`;

// Button with additional features
export const Button = styled(BaseButton)`
  ${(props) =>
    props.danger &&
    `
    background-color: rgba(244, 67, 54, 0.9);
    
    &:hover {
      background-color: rgba(244, 67, 54, 1);
    }
  `}

  ${(props) =>
    props.secondary &&
    `
    background-color: rgba(15, 23, 42, 0.6);
    
    &:hover {
      background-color: rgba(15, 23, 42, 0.8);
    }
  `}
`;

export const ConfirmationMessagesContainer = styled.div`
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ConfirmationMessage = styled.div`
  padding: 10px 15px;
  background-color: ${(props) =>
    props.type === "accepted"
      ? "rgba(76, 175, 80, 0.1)"
      : "rgba(244, 67, 54, 0.1)"};
  border-left: 3px solid
    ${(props) => (props.type === "accepted" ? "#4caf50" : "#f44336")};
  border-radius: 4px;
  font-size: 14px;
  position: relative;
  padding-right: 30px;
`;

export const VideoContainer = styled.div`
  margin-top: 15px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
`;

export const SuggestedCompaniesCard = styled(DashboardCard)`
  max-height: 600px;
  overflow-y: auto;
`;

export const VideoStatus = styled.div`
  padding: 1rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  margin-bottom: 1rem;
`;

export const VideoControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
`;

export const VideoButton = styled(BaseButton)`
  background: rgba(99, 102, 241, 0.2);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.3);
  padding: 0.75rem 1.5rem;

  &:hover {
    background: rgba(99, 102, 241, 0.3);
    transform: translateY(-2px);
  }
`;

export const CompanyCard = styled.div`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  margin-bottom: 1rem;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;

export const CompanyLogo = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: cover;
  margin-right: 1rem;
`;

export const CompanyInfo = styled.div`
  flex: 1;
`;

export const CompanyDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin: 0.5rem 0;
  font-size: 0.9rem;
  line-height: 1.5;
`;

export const CompanyTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0;
`;

export const CompanyTag = styled.span`
  padding: 0.25rem 0.5rem;
  background: rgba(99, 102, 241, 0.2);
  color: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  font-size: 0.8rem;
`;

export const ApplyButton = styled(BaseButton)`
  background: rgba(79, 70, 229, 0.7);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.3);
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  margin-top: 0.5rem;
  display: block;
  margin-left: auto;
  margin-right: auto;
  text-align: center;

  &:hover:not(:disabled) {
    background: rgba(79, 70, 229, 0.9);
    transform: translateY(-3px);
  }
`;
